import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Plus } from 'iconoir-react';
import ExpensesPage from './ExpensesPage';
import PaymentsPage from './PaymentsPage';
import FamilyContributionCard from '../components/budget/FamilyContributionCard';

// Earthy palette tuned to the Shaadi Planner brand. Reused across pie slices
// for the "By category" view; the simpler "Spent vs Remaining" view only
// needs the first two.
// Brand palette (pinks/beiges/mauves only) — May 2026 rebrand.
const PALETTE = [
  '#D1B0AB', // dusty rose (brand flagship)
  '#9D8089', // deeper mauve
  '#D4B896', // warm beige
  '#C5B0BA', // lavender mauve
  '#BFA88A', // khaki beige
  '#8F7480', // dusty plum
  '#E8C4C0', // light pink
  '#A89890', // taupe
];

function fmtMoney(n, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);
}

/** The hero pie chart at the top of the Budget page. Toggles between
 *  "Spent vs Remaining" (2 slices) and "By Category" (N slices).
 *  Self-shrinks gracefully on small screens.
 *
 *  `committed` = paid + awaiting (i.e. all expenses you've decided to spend,
 *  whether the cheque has cleared or not). This is what the pie shows
 *  against the total budget so users see real budget impact the moment
 *  an expense is added — the user explicitly asked for this behaviour.
 */
function BudgetPie({ totalBudget, committed, paid, awaiting, byCategory, currency, mode, onModeChange, onQuickAdd }) {
  const remaining = Math.max(0, totalBudget - committed);
  const overBudget = committed > totalBudget && totalBudget > 0;

  // For "Spent vs Remaining": split the committed slice into paid + awaiting
  // sub-slices so the chart shows three states at once.
  const splitData = useMemo(() => {
    if (!totalBudget) {
      // No budget set — fall back to a meaningful single-slice render.
      return [{ name: 'Committed', value: Math.max(committed, 1) }];
    }
    const slices = [];
    if (paid > 0) slices.push({ name: 'Paid', value: paid });
    if (awaiting > 0) slices.push({ name: 'Awaiting payment', value: awaiting });
    slices.push({ name: 'Remaining', value: remaining });
    return slices;
  }, [totalBudget, committed, paid, awaiting, remaining]);

  const categoryData = useMemo(() => {
    return Object.entries(byCategory || {})
      .map(([name, value]) => ({ name, value }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [byCategory]);

  const data = mode === 'category' ? categoryData : splitData;
  const hasData = data.length > 0;

  const pct = totalBudget > 0 ? Math.round((committed / totalBudget) * 100) : 0;

  // Color map for the split mode. Paid = moss (positive done), Awaiting =
  // ochre (caution — money owed), Remaining = neutral cream.
  const splitColor = (name) => {
    if (name === 'Paid') return '#9D8089';
    if (name === 'Awaiting payment') return '#D4B896';
    if (name === 'Remaining') return '#CCC2BC';
    return '#D1B0AB'; // committed (no-budget fallback)
  };

  return (
    <Card className="card-surface p-6" data-testid="budget-pie-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl text-charcoal">Budget overview</h2>
          <p className="text-sm text-muted mt-1">
            {totalBudget > 0
              ? `${fmtMoney(committed, currency)} committed of ${fmtMoney(totalBudget, currency)} (${pct}%)`
              : `${fmtMoney(committed, currency)} tracked so far — set a budget on the wedding to see a target.`}
          </p>
          {overBudget && (
            <p className="text-xs text-terracotta mt-1 font-medium" data-testid="budget-over-warning">
              Over budget by {fmtMoney(committed - totalBudget, currency)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Quick add expense — anchored next to the chart so the most common
              high-frequency action stays in reach without scrolling to the tab. */}
          <Button
            onClick={onQuickAdd}
            className="btn-primary flex items-center gap-2 text-xs"
            data-testid="quick-add-expense-btn"
          >
            <Plus className="w-4 h-4" /> Quick add expense
          </Button>
          {/* Two-tab toggle. Plain buttons keep the design clean — we don't need
              a full Select for two states. */}
          <div className="inline-flex border border-border rounded-sm overflow-hidden">
            <button
              onClick={() => onModeChange('split')}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                mode === 'split' ? 'bg-charcoal text-page' : 'bg-page text-muted hover:text-charcoal'
              }`}
              data-testid="budget-pie-mode-split"
            >
              Spent vs remaining
            </button>
            <button
              onClick={() => onModeChange('category')}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                mode === 'category' ? 'bg-charcoal text-page' : 'bg-page text-muted hover:text-charcoal'
              }`}
              data-testid="budget-pie-mode-category"
            >
              By category
            </button>
          </div>
        </div>
      </div>

      {hasData ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {data.map((entry, i) => (
                    <Cell
                      key={entry.name}
                      fill={
                        mode === 'split'
                          ? splitColor(entry.name)
                          : PALETTE[i % PALETTE.length]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => fmtMoney(value, currency)}
                  contentStyle={{ background: '#fff', border: '1px solid #CCC2BC', borderRadius: 4, fontSize: 12 }}
                />
                {mode === 'category' && <Legend wrapperStyle={{ fontSize: 11, color: '#7A7975' }} />}
              </PieChart>
            </ResponsiveContainer>
          </div>
          {/* Right column: textual breakdown — easier to scan than the chart alone. */}
          <div className="space-y-2" data-testid="budget-pie-breakdown">
            {(mode === 'split' ? splitData : categoryData.slice(0, 6)).map((d, i) => (
              <div key={d.name} className="flex items-center gap-3 text-sm">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{
                    background:
                      mode === 'split'
                        ? splitColor(d.name)
                        : PALETTE[i % PALETTE.length],
                  }}
                />
                <span className="text-charcoal flex-1 truncate">{d.name}</span>
                <span className="font-mono text-charcoal">{fmtMoney(d.value, currency)}</span>
              </div>
            ))}
            {mode === 'category' && categoryData.length > 6 && (
              <p className="text-[11px] text-muted">
                +{categoryData.length - 6} more categories
              </p>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted py-8 text-center">
          Add expenses or payment plans below to see your budget breakdown.
        </p>
      )}
    </Card>
  );
}

export default function BudgetPage() {
  const { currentWedding, expenses, fetchExpenses } = useWedding();
  const [mode, setMode] = useState('split');
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState('expenses');
  const expensesRef = useRef(null);

  // "Quick add expense" — switches to the Expenses tab (so the user sees the
  // resulting row appear) then opens the existing modal via imperative ref.
  // Using a 60ms delay gives the tab transition a beat to mount the inner
  // ExpensesPage if it wasn't already rendered.
  const handleQuickAddExpense = () => {
    setTab('expenses');
    setTimeout(() => expensesRef.current?.openAddModal(), 60);
  };

  useEffect(() => {
    if (currentWedding) fetchExpenses(currentWedding.id);
  }, [currentWedding, fetchExpenses]);

  // Pull payment plans separately so the pie can include unpaid installments
  // alongside the discrete expenses. Failures are silent: the pie still
  // works with just expenses while the network catches up.
  useEffect(() => {
    let cancel = false;
    const run = async () => {
      if (!currentWedding) return;
      try {
        const axios = (await import('axios')).default;
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/payment-schedule/${currentWedding.id}`,
          { withCredentials: true },
        );
        if (!cancel) setPayments(Array.isArray(data) ? data : []);
      } catch {
        if (!cancel) setPayments([]);
      }
    };
    run();
    return () => { cancel = true; };
  }, [currentWedding, tab]); // refetch when user switches tabs (cheap + keeps numbers fresh after edits)

  // Per the user spec: an expense is deducted from the overall budget the
  // moment it's added — paid or not. We therefore split the running totals:
  //   paid       = expenses + installments already settled
  //   awaiting   = expenses + installments committed but not yet paid
  //   committed  = paid + awaiting (drives the pie's "Spent" portion and the
  //                "remaining" calculation downstream).
  // Expenses auto-mirrored from a payment (`auto_from_payment_id`) are
  // skipped on the expense pass to avoid double-counting against the
  // installment they came from.
  const { paid, awaiting, committed, byCategory } = useMemo(() => {
    const cat = {};
    let paidSum = 0;
    let awaitingSum = 0;

    for (const e of expenses || []) {
      if (e.auto_from_payment_id) continue; // counted on the payment side
      const amt = Number(e.amount) || 0;
      if (e.paid) paidSum += amt;
      else awaitingSum += amt;
      // Always count toward category breakdown (whether paid or not — gives a
      // forward-looking view of where the money is going).
      if (e.category) cat[e.category] = (cat[e.category] || 0) + amt;
    }

    for (const p of payments || []) {
      const insts = Array.isArray(p.installments) ? p.installments : [];
      const planCategory = p.category || 'Vendor payments';
      let planTotal = 0;
      for (const inst of insts) {
        const amt = Number(inst.amount) || 0;
        planTotal += amt;
        if (inst.paid) paidSum += amt;
        else awaitingSum += amt;
      }
      if (planTotal > 0) cat[planCategory] = (cat[planCategory] || 0) + planTotal;
    }

    return {
      paid: paidSum,
      awaiting: awaitingSum,
      committed: paidSum + awaitingSum,
      byCategory: cat,
    };
  }, [expenses, payments]);

  const totalBudget = Number(currentWedding?.total_budget) || 0;
  const remaining = Math.max(0, totalBudget - committed);
  const currency = currentWedding?.preferred_currency || 'USD';

  if (!currentWedding) {
    return (
      <Layout>
        <div className="text-center py-12 text-muted">
          Please select or create a wedding first.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="budget-page">
        {/* Page header */}
        <div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight">
            Budget
          </h1>
          <p className="text-muted font-light mt-2">
            One place for every expense, payment plan, and vendor bill.
          </p>
        </div>

        {/* Three-metric summary — Paid · Awaiting Payment · Remaining Budget.
            Renders the user's mental model directly: every committed expense
            already affects the remaining figure regardless of payment status. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="budget-summary-cards">
          <Card className="card-surface p-6" data-testid="budget-summary-paid">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-moss" />
              <p className="text-xs text-muted uppercase tracking-wider">Paid</p>
            </div>
            <p className="font-mono text-3xl text-moss tracking-tight">
              {fmtMoney(paid, currency)}
            </p>
          </Card>
          <Card className="card-surface p-6" data-testid="budget-summary-awaiting">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-ochre" />
              <p className="text-xs text-muted uppercase tracking-wider">Awaiting Payment</p>
            </div>
            <p className="font-mono text-3xl text-ochre tracking-tight">
              {fmtMoney(awaiting, currency)}
            </p>
          </Card>
          <Card className="card-surface p-6" data-testid="budget-summary-remaining">
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2 h-2 rounded-full ${committed > totalBudget && totalBudget > 0 ? 'bg-terracotta' : 'bg-charcoal'}`} />
              <p className="text-xs text-muted uppercase tracking-wider">Remaining Budget</p>
            </div>
            <p className={`font-mono text-3xl tracking-tight ${committed > totalBudget && totalBudget > 0 ? 'text-terracotta' : 'text-charcoal'}`}>
              {fmtMoney(remaining, currency)}
            </p>
            {totalBudget > 0 && (
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted mt-1">
                of {fmtMoney(totalBudget, currency)} total
              </p>
            )}
          </Card>
        </div>

        {/* Hero pie chart */}
        <BudgetPie
          totalBudget={totalBudget}
          committed={committed}
          paid={paid}
          awaiting={awaiting}
          byCategory={byCategory}
          currency={currency}
          mode={mode}
          onModeChange={setMode}
          onQuickAdd={handleQuickAddExpense}
        />

        {/* Family contribution split — auto-hides for legacy single-funder
            weddings (no family-budget configured AND no family-tagged
            payments yet). */}
        <FamilyContributionCard
          wedding={currentWedding}
          payments={payments}
          currency={currency}
        />

        {/* Tabs: Expenses (line items) | Payments (vendor installments) */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-page border border-border">
            <TabsTrigger value="expenses" data-testid="budget-tab-expenses">
              Expenses
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="budget-tab-payments">
              Payments
            </TabsTrigger>
          </TabsList>
          <TabsContent value="expenses" className="mt-6">
            <ExpensesPage bare ref={expensesRef} />
          </TabsContent>
          <TabsContent value="payments" className="mt-6">
            <PaymentsPage bare />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
